import React from 'react';
import { useMutation } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { approveGate } from '../api';
import ConceptArtifacts from '../ConceptArtifacts';

// Stage 6: authorizing the build, what has been built, and filing it.
//
// What is available comes from the server (production_gate), computed from the same
// GATE_PREREQS the approve endpoint enforces. This page used to keep its own copy of that
// status list, which is how a concept approved for sale matched no branch and was told
// "Available once the concept clears review" - a priced class with no route to production.
export default function ProductionStage({ concept: c, conceptId, gate, onChanged,
  onEnterProduction, onGoToStage }) {
  const approveGateMut = useMutation({
    mutationFn: (g) => approveGate(conceptId, g),
    onSuccess: (res) => {
      // The endpoint stays tolerant of a double submit, so say which happened rather than
      // reporting a fresh approval for a press that changed nothing.
      toast.success(res?.already_approved ? 'That gate was already approved' : 'Gate approved');
      onChanged();
    },
    onError: (e) => toast.error(e?.message || 'Not permitted for this gate'),
  });

  const prod = c.production;
  // Per-gate state from the server: applicable to this delivery type, already approved,
  // and whether pressing would do anything. GATE_PREREQS includes each gate's own resulting
  // status so a repeat press succeeded indefinitely, which meant the button never stopped
  // being live and every extra click logged another approval of something already approved.
  const gates = c.gates || {};
  const releaseGates = ['publication', 'scheduling'].filter((g) => gates[g]?.applicable);

  return (
    <div className="space-y-5">
      {/* Approve development */}
      {gates.development?.approved && (
        <div className="text-xs px-3 py-1.5 rounded-lg bg-[#0b2a17] text-green-400 border border-[#1c4a30] inline-block">
          ✓ Approved for development
        </div>
      )}

      {gate.can_approve_development && (
        <div>
          <button onClick={() => approveGateMut.mutate('development')} disabled={approveGateMut.isPending}
            className="text-xs px-3 py-1.5 rounded-lg bg-[#1f6feb] text-white disabled:opacity-40">
            {approveGateMut.isPending ? 'Approving…' : 'Approve for development'}
          </button>
          <div className="text-[10px] text-[#5f6b7e] mt-1 max-w-md">
            Approving development authorizes building the class; produce and refine it in the
            production workspace. Selling happens after the class exists.
          </div>
        </div>
      )}

      {/* Why it is not available, said plainly rather than as a disabled button. When the
          class target is the only thing missing, this offers the jump rather than naming a
          stage and leaving the author to find it. */}
      {!gate.can_approve_development && !gate.can_open_workspace && (
        <div className="text-[11px] text-[#f7a23b]">
          <div>
            {gate.blocked_reason
              || (gate.approver_permitted === false
                ? 'Waiting on a Director to approve development.'
                : 'Production opens once this concept clears review.')}
          </div>
          {gate.development_pending_class_target && onGoToStage && (
            <button onClick={() => onGoToStage('match')}
              className="mt-2 text-[11px] px-2 py-1 rounded bg-[#1f2a38] border border-[#2b3a4d] text-[#5ea9ff]">
              Go to Match &amp; target →
            </button>
          )}
        </div>
      )}

      {gate.can_open_workspace && (
        <>
          <div>
            <ConceptArtifacts
              production={prod}
              onOpen={(artifactId) => onEnterProduction({ artifactId })}
              onAdd={() => onEnterProduction({ add: true })} />
            <button onClick={() => onEnterProduction()}
              className="text-xs px-3 py-1.5 rounded-lg bg-[#1f6feb] text-white">
              Open production workspace →
            </button>
          </div>

          {/* Publication (elearning) or scheduling (live), plus write-back. These also live
              in the production workspace; they are here so the rail can complete the class
              without leaving it. */}
          <div className="border-t border-[#1d2634] pt-4">
            <div className="text-[10px] uppercase tracking-wide text-[#7d8aa0] mb-2">Release gates</div>
            <div className="flex flex-wrap items-center gap-2">
              {releaseGates.map((g) => {
                const state = gates[g] || {};
                // Approved: a state, not a button. Pressing it again did nothing but add a
                // duplicate line to the activity trail.
                if (state.approved) {
                  return (
                    <span key={g} className="text-xs px-3 py-1.5 rounded-lg bg-[#0b2a17] text-green-400 border border-[#1c4a30]">
                      ✓ Approved for {g}
                    </span>
                  );
                }
                if (!state.can_approve) {
                  return (
                    <span key={g} className="text-[11px] text-[#7d8aa0]">
                      {state.blocked_reason || `Approval for ${g} is not available yet.`}
                    </span>
                  );
                }
                return (
                  <button key={g} onClick={() => approveGateMut.mutate(g)} disabled={approveGateMut.isPending}
                    className="text-xs px-3 py-1.5 rounded-lg bg-[#1f2a38] border border-[#2b3a4d] disabled:opacity-40">
                    {approveGateMut.isPending ? 'Approving…' : `Approve for ${g}`}
                  </button>
                );
              })}
            </div>
            <div className="text-[10px] text-[#5f6b7e] mt-2 max-w-md">
              {gates.scheduling?.applicable
                ? 'Scheduling opens the calendar for this class. Sessions are booked in the Scheduling stage.'
                : 'Publication marks the class live in the LMS. JPRO files the documents to SharePoint; the LMS upload itself is manual.'}
            </div>
            <div className="text-[10px] text-[#5f6b7e] mt-1 max-w-md">
              Filing to SharePoint happens in the production workspace, which is also where a
              build can be restarted or stopped.
            </div>
          </div>
        </>
      )}
    </div>
  );
}
